import type {
  DispatchAppendNewLegCommand,
  DispatchAppendNewLegDetails,
  DispatchAppendNewLegRequest,
  DispatchFreightValidationIssue,
  DispatchManifestParty,
  DispatchManifestAppendTarget,
  DispatchRateLocation,
  DispatchRatingFreight,
  DispatchRatingItem,
  DispatchRatingRequest,
} from '../models/dispatch-freight.models';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DETAIL_FIELDS: Array<keyof DispatchAppendNewLegDetails> = [
  'load_start',
  'load_end',
  'load_details',
  'load_special_info',
  'requirements',
  'customer_po',
  'pickup_reference_number',
  'delivery_reference_number',
  'booking_number',
  'release_number',
];

const TEXT_DETAIL_FIELDS: Array<keyof Omit<DispatchAppendNewLegDetails, 'load_start' | 'load_end'>> = [
  'load_details',
  'load_special_info',
  'requirements',
  'customer_po',
  'pickup_reference_number',
  'delivery_reference_number',
  'booking_number',
  'release_number',
];

const LOCAL_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

export class DispatchManifestLegValidationError extends Error {
  readonly issues: DispatchFreightValidationIssue[];

  constructor(issues: DispatchFreightValidationIssue[]) {
    super('The freight draft is not ready to append to a manifest.');
    this.name = 'DispatchManifestLegValidationError';
    this.issues = issues;
  }
}

/**
 * Adapts the normalized rating contract to the narrower append-new-leg command.
 * Customer, domain, lading identity and sequence remain server-owned.
 */
export function buildDispatchAppendNewLegCommand(
  target: DispatchManifestAppendTarget,
  rating: DispatchRatingRequest,
  details: DispatchAppendNewLegDetails = {},
): DispatchAppendNewLegCommand {
  const issues: DispatchFreightValidationIssue[] = [];
  const manifestUuid = requiredUuid(target.manifest_uuid, 'manifest_uuid', issues);

  if (!Number.isInteger(target.manifest_version) || target.manifest_version < 1) {
    issues.push({
      path: 'manifest_version',
      message: 'Load the current positive manifest version before appending a leg.',
    });
  }

  const predecessorUuid = optionalUuid(
    target.predecessor_lading_uuid,
    'predecessor_lading_uuid',
    issues,
  );
  const shipper = party(rating.origin, 'shipper', issues);
  const consignee = party(rating.destination, 'consignee', issues);
  const freight = normalizeFreight(rating.freight, issues);
  validateFreight(rating, issues);
  const normalizedDetails = normalizeDetails(details, issues);

  if (issues.length > 0) {
    throw new DispatchManifestLegValidationError(issues);
  }

  const request: DispatchAppendNewLegRequest = {
    expected_manifest_version: target.manifest_version,
    shipper: shipper as DispatchManifestParty,
    consignee: consignee as DispatchManifestParty,
    freight,
  };

  if (predecessorUuid) {
    request.predecessor_lading_uuid = predecessorUuid;
  }
  if (rating.freight_items && rating.freight_items.length > 0) {
    request.freight_items = rating.freight_items.map(cloneItem);
  }

  DETAIL_FIELDS.forEach((field) => {
    const value = normalizedDetails[field];
    if (value) {
      request[field] = value;
    }
  });

  return {
    manifest_uuid: manifestUuid,
    request,
  };
}

function normalizeFreight(
  source: DispatchRatingFreight,
  issues: DispatchFreightValidationIssue[],
): DispatchRatingFreight {
  const freight: DispatchRatingFreight = { ...source };
  if (source.load_type === undefined) {
    return freight;
  }

  const loadType = cleanString(source.load_type).toUpperCase();
  if (!loadType) {
    delete freight.load_type;
  } else if (loadType === 'TL') {
    freight.load_type = 'FTL';
  } else if (loadType === 'LTL' || loadType === 'FTL') {
    freight.load_type = loadType;
  } else {
    issues.push({
      path: 'freight.load_type',
      message: 'freight.load_type must be LTL or FTL.',
    });
  }

  return freight;
}

function normalizeDetails(
  details: DispatchAppendNewLegDetails,
  issues: DispatchFreightValidationIssue[],
): DispatchAppendNewLegDetails {
  const normalized: DispatchAppendNewLegDetails = {};
  const loadStart = normalizeLocalDateTime(details.load_start, 'load_start', issues);
  const loadEnd = normalizeLocalDateTime(details.load_end, 'load_end', issues);

  if (loadStart) normalized.load_start = loadStart;
  if (loadEnd) normalized.load_end = loadEnd;
  if (loadStart && loadEnd && loadEnd < loadStart) {
    issues.push({ path: 'load_end', message: 'load_end must be on or after load_start.' });
  }

  TEXT_DETAIL_FIELDS.forEach((field) => {
    const value = details[field];
    if (value === null || value === undefined || value === '') {
      return;
    }
    if (typeof value !== 'string') {
      issues.push({ path: field, message: `${field} must be text.` });
      return;
    }

    const cleanValue = value.trim();
    if (cleanValue) {
      normalized[field] = cleanValue;
    }
  });

  return normalized;
}

function normalizeLocalDateTime(
  value: unknown,
  path: 'load_start' | 'load_end',
  issues: DispatchFreightValidationIssue[],
): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    issues.push({ path, message: `${path} must be a local date or local date-time.` });
    return undefined;
  }

  const raw = value.trim();
  if (!raw) {
    return undefined;
  }

  const match = LOCAL_DATE_TIME_PATTERN.exec(raw);
  if (!match) {
    issues.push({
      path,
      message: `${path} must use YYYY-MM-DD or YYYY-MM-DD HH:mm[:ss] without a time-zone offset.`,
    });
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4] ?? '0');
  const minute = Number(match[5] ?? '0');
  const second = Number(match[6] ?? '0');
  const calendar = new Date(0);
  calendar.setUTCHours(hour, minute, second, 0);
  calendar.setUTCFullYear(year, month - 1, day);

  if (
    calendar.getUTCFullYear() !== year ||
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day ||
    calendar.getUTCHours() !== hour ||
    calendar.getUTCMinutes() !== minute ||
    calendar.getUTCSeconds() !== second
  ) {
    issues.push({ path, message: `${path} is not a valid calendar date-time.` });
    return undefined;
  }

  return `${match[1]}-${match[2]}-${match[3]} ${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function party(
  location: DispatchRateLocation,
  path: 'shipper' | 'consignee',
  issues: DispatchFreightValidationIssue[],
): DispatchManifestParty | null {
  const contactUuid = requiredUuid(location.contact_uuid, `${path}.contact_uuid`, issues);
  const addressUuid = requiredUuid(
    location.contact_address_uuid,
    `${path}.contact_address_uuid`,
    issues,
  );

  return contactUuid && addressUuid
    ? { contact_uuid: contactUuid, contact_address_uuid: addressUuid }
    : null;
}

function validateFreight(
  rating: DispatchRatingRequest,
  issues: DispatchFreightValidationIssue[],
): void {
  const items = rating.freight_items ?? [];
  const hasUsableMetric = [
    rating.freight.total_weight_lb,
    rating.freight.linear_feet,
    rating.freight.handling_units,
    rating.freight.cubic_feet,
  ].some((value) => typeof value === 'number' && Number.isFinite(value) && value > 0);

  if (items.length === 0 && !hasUsableMetric) {
    issues.push({
      path: 'freight',
      message: 'Provide at least one freight item or a positive freight measurement.',
    });
  }

  items.forEach((item, index) => {
    if (item.source === 'catalog') {
      requiredUuid(
        item.customer_product_uuid,
        `freight_items.${index}.customer_product_uuid`,
        issues,
      );
    }
  });
}

function cloneItem(item: DispatchRatingItem): DispatchRatingItem {
  return { ...item };
}

function requiredUuid(
  value: unknown,
  path: string,
  issues: DispatchFreightValidationIssue[],
): string {
  const normalized = cleanString(value).toLowerCase();
  if (!UUID_PATTERN.test(normalized)) {
    issues.push({ path, message: `${path} must be a valid UUID.` });
    return '';
  }

  return normalized;
}

function optionalUuid(
  value: unknown,
  path: string,
  issues: DispatchFreightValidationIssue[],
): string | undefined {
  if (!cleanString(value)) {
    return undefined;
  }

  return requiredUuid(value, path, issues);
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
